import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {
  EXPERIMENT_CONFIGS,
  EXPERIMENT_ONBOARDING_ADD_GIFTS_DESIGN,
} from '../../../../constants/experiments';
import useExperiment from '../../../../hooks/useExperiment';
import getOverrideParams from '../../../../utils/parseUrlParams';

const ADD_GIFTS_PATH = '/onboarding/add-gifts';

const useOnboardingAddGiftsDesignExperiment = () => {
  const {pathname} = useLocation();
  const [hasQualified, setHasQualified] = useState(false);
  const experimentName = EXPERIMENT_ONBOARDING_ADD_GIFTS_DESIGN;
  const config =
    EXPERIMENT_CONFIGS[experimentName]?.[process.env.ENV] ??
    EXPERIMENT_CONFIGS[experimentName]?.qa;
  const {experimentId, variants} = config ?? {};
  const controlVariantId = variants?.control?.id;
  const variantId = variants?.variant1?.id;
  const override = getOverrideParams(experimentId);
  const hasOverride = typeof override === 'string';

  /**
   * Only Add Gifts shows this experiment UI. Other routes must not call
   * Flipper, or members get bucketed before they ever see the page.
   */
  const meetsBucketingCriteria = pathname === ADD_GIFTS_PATH;
  const isEligible = hasQualified || meetsBucketingCriteria;
  const shouldUseExperiment = !hasOverride && isEligible && !!experimentId;
  const {
    isLoading,
    isActive,
    variant: hookVariant,
    isInControl: hookIsInControl,
  } = useExperiment({
    name: experimentName,
    isEnabled: shouldUseExperiment,
  });

  /**
   * Stickiness persists an actual assignment: once Flipper has bucketed the
   * member on Add Gifts, navigating away cannot disable the experiment and
   * drop the assignment. Other routes never trigger bucketing.
   */
  useEffect(() => {
    if (meetsBucketingCriteria && isActive && !hasQualified) {
      setHasQualified(true);
    }
  }, [meetsBucketingCriteria, isActive, hasQualified]);

  const resolvedVariant = hasOverride ? override : hookVariant;

  const isExperimentLoading = useMemo(() => {
    if (hasOverride) return false;
    if (!experimentId) return false;
    if (!isEligible) return false;

    return isLoading;
  }, [hasOverride, experimentId, isEligible, isLoading]);

  const isInVariant = useMemo(() => {
    if (!variantId) return false;

    if (hasOverride) {
      return override?.toString().trim() === variantId.toString().trim();
    }

    if (!isActive) return false;

    return resolvedVariant === variantId;
  }, [variantId, hasOverride, override, isActive, resolvedVariant]);

  const isInControl = useMemo(() => {
    if (!controlVariantId) return false;

    if (hasOverride) {
      return override?.toString().trim() === controlVariantId.toString().trim();
    }

    if (!isActive) return true;

    return !!hookIsInControl;
  }, [controlVariantId, hasOverride, override, isActive, hookIsInControl]);

  return useMemo(() => {
    return {
      experimentId,
      variant: resolvedVariant,
      isInVariant,
      isInControl,
      isExperimentLoading,
    };
  }, [
    experimentId,
    resolvedVariant,
    isInVariant,
    isInControl,
    isExperimentLoading,
  ]);
};

export default useOnboardingAddGiftsDesignExperiment;
